import { useEffect, useState } from "react";

// Shared confirmation modal. When `requireReason` is set the confirm button stays
// disabled until the reviewer types something - the backend rejects a REJECT
// decision without a reason, so blocking it here avoids a pointless round trip.
// `children` renders between the description and the reason box for decisions
// that need an extra field (the approve flow puts the last-working-day picker
// there); the caller owns that field's state, so it survives independently of
// the reason box being reset on open.
const ReasonDialog = ({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  confirmClassName = "bg-blue-primary hover:bg-blue-500",
  requireReason = false,
  reasonLabel = "Reason",
  placeholder = "",
  isLoading = false,
  confirmDisabled = false,
  children,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  const disabled = isLoading || confirmDisabled || (requireReason && !reason.trim());

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}

        {children}

        {requireReason && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-600">
              {reasonLabel} <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              placeholder={placeholder}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full resize-none rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-blue-primary"
            />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={disabled}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${confirmClassName}`}
          >
            {isLoading ? "Please wait..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReasonDialog;
